import * as Toast from "@radix-ui/react-toast";
import { Trash, MapPin } from "lucide-react";
import { useState } from "react";
import { RegionEditDialog } from "../region/RegionEditDialog";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface RegionCardProps {
  id: number;
  name: string;
  observation: string;
  onRegionUpdate: () => void;
}

export function RegionCard({
  id,
  name,
  observation,
  onRegionUpdate,
}: RegionCardProps) {
  const [toastOpen, setToastOpen] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  async function handleDeleteRegion() {
    setLoading(true);
    try {
      const route = import.meta.env.VITE_URL_ENDPOINT;
      const response = await fetch(`${route}/api/regions/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete region");
      }
      onRegionUpdate();
      setToastType("success");
      setToastMessage("Região excluída com sucesso!");
      setToastOpen(true);
    } catch (error) {
      setToastOpen(true);
      setToastType("error");
      setToastMessage("Falha ao excluir a região!");
    } finally {
      setLoading(false);
      setOpenConfirm(false);
    }
  }

  return (
    <div className="w-full">
      <Toast.Provider swipeDirection="right">
        <div className="group mb-4 flex flex-col items-start justify-between rounded-xl border border-gray-100 bg-white p-6 shadow-md transition-all duration-200 hover:shadow-lg sm:flex-row sm:items-center">
          <div className="flex-1">
            <div className="mb-1 flex items-center gap-2">
              <MapPin className="text-blue-600" size={20} />
              <h2 className="text-lg font-bold text-gray-900">{name}</h2>
            </div>
            <p className="flex items-center gap-1 text-sm text-gray-600">
              <span className="font-medium text-gray-500">Observação:</span>
              {observation || (
                <span className="italic text-gray-400">Sem observação</span>
              )}
            </p>
          </div>
          <div className="mt-4 flex items-center gap-2 sm:mt-0">
            <RegionEditDialog
              id={id}
              currentName={name}
              currentObservation={observation}
              onRegionUpdate={onRegionUpdate}
            />
            <Dialog open={openConfirm} onOpenChange={setOpenConfirm}>
              <DialogTrigger asChild>
                <Button
                  className="rounded-full bg-red-50 p-2 transition hover:bg-red-100"
                  onClick={() => setOpenConfirm(true)}
                  title="Excluir região"
                >
                  <Trash className="text-red-600" />
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-md bg-white p-4 shadow-lg sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold">
                    Confirmar Exclusão
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500">
                    Tem certeza que deseja excluir esta região? Esta ação não
                    pode ser desfeita.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    className="bg-red-600 text-white"
                    onClick={handleDeleteRegion}
                    disabled={loading}
                  >
                    {loading ? "Excluindo..." : "Excluir"}
                  </Button>
                  <Button
                    className="bg-gray-300"
                    onClick={() => setOpenConfirm(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        <Toast.Root
          open={toastOpen}
          onOpenChange={setToastOpen}
          duration={5000}
          className={`fixed bottom-0 right-0 m-6 rounded-md p-4 shadow-md ${
            toastType === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          <Toast.Description>{toastMessage}</Toast.Description>
          <Toast.Close className="absolute right-1 top-1" />
        </Toast.Root>
        <Toast.Viewport />
      </Toast.Provider>
    </div>
  );
}
